import {
	confirmSignIn,
	fetchAuthSession,
	fetchUserAttributes,
	getCurrentUser,
	signIn,
	signOut,
	type AuthSession,
	type SignInOutput
} from "aws-amplify/auth";
import { toast } from "sonner";
import { LOGOUT_PATH, NEW_PASSWORD_REQUIRED_STEP } from "~/lib/auth-utils";

function isAlreadyAuthenticatedError(error: unknown) {
	if (!error || typeof error !== "object") return false;
	const name = "name" in error ? String(error.name) : "";
	const message = "message" in error ? String(error.message) : "";
	return (
		name === "UserAlreadyAuthenticatedException" ||
		message.toLowerCase().includes("already a signed in user") ||
		message.toLowerCase().includes("already logged in")
	);
}

async function clientSignOut() {
	try {
		await signOut();
	} catch {
		// Local Amplify state may already be empty.
	}
}

async function tryReuseExistingSession(email: string) {
	try {
		await getCurrentUser();
	} catch {
		return false;
	}

	try {
		const attrs = await fetchUserAttributes();
		const currentEmail = attrs.email?.toLowerCase();
		if (currentEmail && currentEmail !== email.toLowerCase()) {
			await clientSignOut();
			return false;
		}

		const session = await fetchAuthSession({ forceRefresh: true });
		if (session.tokens?.idToken) {
			return true;
		}

		await clientSignOut();
		return false;
	} catch {
		await clientSignOut();
		return false;
	}
}

function handleSignInError(err: Error) {
	let message: string;
	switch (err.name) {
		case "NotAuthorizedException":
			message = "Username and password do not match.";
			break;
		case "TooManyRequestsException":
			message = "Login attempt limit exceeded. Please try again later.";
			break;
		default:
			message = err.message || "Unable to sign in. Please try again.";
			console.error(err);
			break;
	}
	toast(message);
	return message;
}

export async function authSignIn(email: string, password: string) {
	const reused = await tryReuseExistingSession(email);
	if (reused) {
		return {
			isSignedIn: true,
			nextStep: { signInStep: "DONE" }
		} as SignInOutput;
	}

	try {
		return await signIn({
			username: email,
			password
		});
	} catch (error) {
		if (isAlreadyAuthenticatedError(error)) {
			await clientSignOut();
			try {
				return await signIn({
					username: email,
					password
				});
			} catch (retryError) {
				handleSignInError(retryError as Error);
				throw retryError;
			}
		}

		handleSignInError(error as Error);
		throw error;
	}
}

export function needsNewPassword(
	result: SignInOutput | { isSignedIn: boolean }
) {
	return (
		"nextStep" in result &&
		result.nextStep?.signInStep === NEW_PASSWORD_REQUIRED_STEP
	);
}

export async function confirmNewPassword(newPassword: string) {
	return confirmSignIn({ challengeResponse: newPassword });
}

export async function getClientAuthSession() {
	try {
		const session = await fetchAuthSession();
		const token = session.tokens?.idToken?.toString();
		if (!token) {
			return { status: "missing" as const };
		}
		return { status: "ok" as const, session, token };
	} catch (error) {
		const message = error instanceof Error ? error.message : String(error);
		const lower = message.toLowerCase();
		if (lower.includes("refresh token") || lower.includes("no current user")) {
			await clientSignOut();
			return { status: "expired" as const };
		}
		return { status: "missing" as const };
	}
}

export function buildAuthFormData(session: AuthSession, token: string) {
	const formData = new FormData();
	formData.append(
		"auth_cookie",
		JSON.stringify({ signedIn: true, userSub: session.userSub ?? null })
	);
	formData.append("token", token);
	return formData;
}

export async function expireClientSession() {
	await clientSignOut();
	if (typeof window !== "undefined") {
		window.location.assign(LOGOUT_PATH);
	}
}

export { clientSignOut };
